import { ProcessAssignmentActivityResponse } from '@6grain/masofa-monolith-api-client/common/model/processAssignmentActivityResponse';
import {
  ProcessActivitySnapshot,
  ProcessCatalogView,
} from '@app/pages/admin/orchestration-process-flows.util';
import { canonicalProcessKey } from '@app/pages/admin/orchestration-process-keys.util';

export const PROCESS_ACTIVITY_POLL_MS = 15_000;

export type ProcessActivityView = ProcessActivitySnapshot;

export function processActivityHasError(
  lastSucceededAt: string | null | undefined,
  lastFailedAt: string | null | undefined,
): boolean {
  const failedMs = parseTime(lastFailedAt);
  if (failedMs == null) {
    return false;
  }
  const succeededMs = parseTime(lastSucceededAt);
  return succeededMs == null || failedMs > succeededMs;
}

export function indexProcessActivity(
  rows: ProcessAssignmentActivityResponse[] | null | undefined,
  formatTime: (iso: string) => string,
): ReadonlyMap<string, ProcessActivityView> {
  const map = new Map<string, ProcessActivityView>();
  for (const row of rows ?? []) {
    const key = canonicalProcessKey(row.processDefinitionKey ?? '');
    if (!key) {
      continue;
    }
    const lastSucceededAt = row.lastSucceededAt ?? null;
    const lastFailedAt = row.lastFailedAt ?? null;
    map.set(key, {
      lastSucceededLabel: lastSucceededAt ? formatTime(lastSucceededAt) : null,
      lastFailedLabel: lastFailedAt ? formatTime(lastFailedAt) : null,
      hasError: processActivityHasError(lastSucceededAt, lastFailedAt),
    });
  }
  return map;
}

export function attachProcessActivity(
  catalog: ProcessCatalogView,
  activity: ReadonlyMap<string, ProcessActivityView>,
): ProcessCatalogView {
  return {
    flows: catalog.flows.map(flow => ({
      ...flow,
      nodes: flow.nodes.map(node => withActivity(node, activity)),
      steps: flow.steps.map(step => ({
        ...step,
        node: withActivity(step.node, activity),
        outgoing: step.outgoing.map(edge => ({
          ...edge,
          source: withActivity(edge.source, activity),
          target: withActivity(edge.target, activity),
        })),
      })),
      edges: flow.edges.map(edge => ({
        ...edge,
        source: withActivity(edge.source, activity),
        target: withActivity(edge.target, activity),
      })),
    })),
    standalone: catalog.standalone.map(process => withActivity(process, activity)),
  };
}

function withActivity<T extends { key: string; activity?: ProcessActivitySnapshot | null }>(
  item: T,
  activity: ReadonlyMap<string, ProcessActivityView>,
): T {
  return {
    ...item,
    activity: activity.get(canonicalProcessKey(item.key)) ?? null,
  };
}

function parseTime(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}
